import type { Category } from '../services/categories';
import type { PortfolioItem } from '../services/portfolio';

/** SEO portfolio landing paths → DB category slugs (best-effort mapping). */
export const PORTFOLIO_SEO_PATHS = [
  '/portfolio/mariage',
  '/portfolio/photobooth',
  '/portfolio/corporate',
  '/portfolio/shooting',
] as const;

export type PortfolioSeoPath = (typeof PORTFOLIO_SEO_PATHS)[number];

export const PORTFOLIO_SEO_CATEGORY_SLUGS: Record<PortfolioSeoPath, string[]> = {
  '/portfolio/mariage': ['mariages'],
  '/portfolio/photobooth': ['evenements', 'mariages'],
  '/portfolio/corporate': ['evenements', 'immobilier'],
  '/portfolio/shooting': ['clips-musicaux', 'mariages', 'gastronomie'],
};

const PHOTOBOOTH_KEYWORDS = /photobooth|photo\s*booth|borne/i;

export function isPortfolioSeoPath(path: string): path is PortfolioSeoPath {
  return (PORTFOLIO_SEO_PATHS as readonly string[]).includes(path);
}

export function getCategorySlugsForPortfolioSeoPath(path: PortfolioSeoPath): string[] {
  return PORTFOLIO_SEO_CATEGORY_SLUGS[path];
}

export function resolveCategoryIdsBySlugs(categories: Category[], slugs: string[]): string[] {
  const slugSet = new Set(slugs);
  return categories.filter((c) => slugSet.has(c.slug)).map((c) => c.id);
}

/** Filter portfolio items for an SEO landing page. */
export function filterPortfolioItemsForSeoPath(
  items: PortfolioItem[],
  categories: Category[],
  seoPath: PortfolioSeoPath,
): PortfolioItem[] {
  const slugs = getCategorySlugsForPortfolioSeoPath(seoPath);
  const categoryIds = new Set(resolveCategoryIdsBySlugs(categories, slugs));

  let filtered = items.filter((item) => item.categoryId && categoryIds.has(item.categoryId));

  if (seoPath === '/portfolio/photobooth' && filtered.length === 0) {
    filtered = items.filter(
      (item) =>
        PHOTOBOOTH_KEYWORDS.test(item.title) ||
        (item.artist ? PHOTOBOOTH_KEYWORDS.test(item.artist) : false),
    );
  }

  return filtered;
}

/** Primary category for CTA → `/portfolio?category=<id>`. */
export function getPrimaryCategoryIdForSeoPath(
  items: PortfolioItem[],
  categories: Category[],
  seoPath: PortfolioSeoPath,
): string | null {
  const slugs = getCategorySlugsForPortfolioSeoPath(seoPath);
  const filtered = filterPortfolioItemsForSeoPath(items, categories, seoPath);

  for (const slug of slugs) {
    const cat = categories.find((c) => c.slug === slug);
    if (cat && filtered.some((item) => item.categoryId === cat.id)) {
      return cat.id;
    }
  }

  const firstWithItems = slugs
    .map((slug) => categories.find((c) => c.slug === slug))
    .find((cat) => cat && filtered.some((item) => item.categoryId === cat.id));

  return firstWithItems?.id ?? resolveCategoryIdsBySlugs(categories, slugs)[0] ?? null;
}

export function getPortfolioCtaHref(
  items: PortfolioItem[],
  categories: Category[],
  seoPath: PortfolioSeoPath,
): string {
  const categoryId = getPrimaryCategoryIdForSeoPath(items, categories, seoPath);
  return categoryId ? `/portfolio?category=${encodeURIComponent(categoryId)}` : '/portfolio';
}

/** Single-category album view when one slug maps cleanly; otherwise carousel rows. */
export function getGalleryFilterCategoryId(
  items: PortfolioItem[],
  categories: Category[],
  seoPath: PortfolioSeoPath,
): string | null {
  const slugs = getCategorySlugsForPortfolioSeoPath(seoPath);
  if (slugs.length !== 1) return null;
  return getPrimaryCategoryIdForSeoPath(items, categories, seoPath);
}
